/** Logik von `BaseDialog/IwmDialogDokumentUpload.razor` — Markup siehe `DocumentUploadDialog`. */

import { useEffect, useState } from 'react';

import { configApi } from '@/api/services/configApi';
import { documentApi } from '@/api/services/documentApi';
import type { UploaderFile } from '@/components/ui';
import { createDocument, type ChinaAppSysConfig, type Document } from '@/types/entities';
import { bytesToBase64 } from '@/utils/file';

const FILE_EXTENSIONS_KEY = 'CONFIG_SEEFRACHT_BLOB_FILE_EXTENSIONS';
const MAX_FILE_SIZE_KEY = 'CONFIG_SEEFRACHT_BLOB_MAX_FILE_SIZE';

export interface DocumentUploadDialogOptions {
  open: boolean;
  prepareModel: (model: Document) => Document;
  onClose: () => void;
  onSuccess?: () => void;
}

export interface DocumentUploadDialogState {
  model: Document;
  /** Kommaliste aus `CONFIG_SEEFRACHT_BLOB_FILE_EXTENSIONS`. */
  allowedFileExtensions: string;
  /** Obergrenze in Bytes; `0` heißt „ohne Grenze". */
  allowedFileSize: number;
  saving: boolean;
  /**
   * Port von `Validate()`: Der Altclient setzt dort eine Meldung, zeigt sie aber nie an —
   * das Ergebnis steuert allein den Zustand des Speichern-Knopfs.
   */
  valid: boolean;
  setBezeichnung: (value: string) => void;
  selectFiles: (files: UploaderFile[]) => Promise<void>;
  removeFile: () => void;
  save: () => Promise<void>;
}

/** Pendant zu `Int32.TryParse`: nur eine reine Ganzzahl gilt als Erfolg. */
function parseInteger(text: string | null): number | null {
  if (text === null || !/^[+-]?\d+$/.test(text.trim())) return null;
  return Number.parseInt(text.trim(), 10);
}

function isEmpty(value: string | null): boolean {
  return value === null || value === '';
}

/** Pendant zu `f.File.OpenReadStream(…).CopyToAsync(memoryStream)`. */
function readBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(
        reader.result instanceof ArrayBuffer ? new Uint8Array(reader.result) : new Uint8Array(),
      );
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Die Datei konnte nicht gelesen werden.'));
    };
    reader.readAsArrayBuffer(file);
  });
}

export function useDocumentUploadDialog(
  options: DocumentUploadDialogOptions,
): DocumentUploadDialogState {
  const { onClose, onSuccess, open, prepareModel } = options;

  const [model, setModel] = useState<Document>(createDocument);
  const [allowedFileExtensions, setAllowedFileExtensions] = useState('');
  const [allowedFileSize, setAllowedFileSize] = useState(0);
  const [saving, setSaving] = useState(false);

  // Port von `OnInitializedAsync`.
  useEffect(() => {
    let active = true;
    const load = async () => {
      const extensions: ChinaAppSysConfig | null = await configApi.getEntry(FILE_EXTENSIONS_KEY);
      if (active && extensions !== null) setAllowedFileExtensions(extensions.Value ?? '');

      const maxSize: ChinaAppSysConfig | null = await configApi.getEntry(MAX_FILE_SIZE_KEY);
      const megabytes = maxSize === null ? null : parseInteger(maxSize.Value);
      if (active && megabytes !== null) setAllowedFileSize(megabytes * 1_000_000);
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  // Port von `Show()`: leeres Modell je Öffnen.
  useEffect(() => {
    if (!open) return;
    setModel(createDocument());
    setSaving(false);
  }, [open]);

  const valid = !isEmpty(model.Bezeichnung) && !isEmpty(model.Binary);

  return {
    model,
    allowedFileExtensions,
    allowedFileSize,
    saving,
    valid,

    setBezeichnung: (value) => {
      setModel((current) => ({ ...current, Bezeichnung: value }));
    },

    // Port von `OnFileValueChange`.
    selectFiles: async (files) => {
      for (const file of files) {
        const bytes = await readBytes(file.file);
        setModel((current) => ({
          ...current,
          Bezeichnung: file.name,
          MimeContentType: file.mimeContentType,
          DateiGroesse: file.size,
          FileName: file.name,
          FileExtension: `.${file.type}`,
          Binary: bytesToBase64(bytes),
        }));
      }
    },

    // Port von `OnFileRemove`.
    removeFile: () => {
      setModel((current) => ({ ...current, Binary: '' }));
    },

    // Port von `Save()`; `BeforeSave()` entspricht `prepareModel`.
    save: async () => {
      if (!valid) return;
      setSaving(true);
      // `ApiDokumentService.Upload` verschluckt jeden Fehler wie `APIBase` — der
      // `catch`-Block des Altclients hat deshalb kein Gegenstück.
      await documentApi.upload(prepareModel(model));
      setSaving(false);
      onClose();
      onSuccess?.();
    },
  };
}
